import { HttpClient } from '@angular/common/http';
import {
  type AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import * as _ from 'lodash';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
} from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import type { MessageSeen } from '../../../server/models/message-seen.model';
import * as TaskTypingIndicator from '../../../server/routes/tasks/typing';
import * as ThreadTypingIndicator from '../../../server/routes/threads/typing';
import type { ActivityStatusStringType } from '../../../server/services/activity-status-constants.service';
import {
  cleanTitle,
  cleanTitleExceptTrim,
  determineDirectUserId,
  generateId,
  generatePath,
} from '../../../server/services/utils.service';
import type { FileElement } from '../../../server/types/file-types';
import type {
  Message,
  ThreadOrTaskMode,
  UserUpdate,
} from '../../../server/types/message-types';
import type { Org, OrgUser } from '../../../server/types/org-types';
import { threadTitleMaxLength } from '../../../server/validation/message-validation';
import { ActivityStatusService } from '../activity-status.service';
import { AuthUserService, unknownUser } from '../auth-user.service';
import { FileService } from '../file.service';
import {
  generateInboxPath,
  generateTaskInboxPath,
  generateTaskPath,
} from '../inbox.service';
import { MenuService } from '../menu.service';
import {
  type DisplayFile,
  type DisplayMessage,
  MessageService,
  type MessageSubmitted,
  ThreadItem,
} from '../message.service';
import { SettingsService, type ThemeString } from '../settings.service';
import { TaskService } from '../task.service';
import {
  type ThreadEditTitleData,
  ThreadEditTitleDialogComponent,
} from '../thread-edit-title-dialog/thread-edit-title-dialog.component';
import {
  type ThreadEditUsersData,
  ThreadEditUsersDialogComponent,
} from '../thread-edit-users-dialog/thread-edit-users-dialog.component';

dayjs.extend(calendar);

const standardPlaceholderText = 'Message';

@Component({
  selector: 'app-thread-view',
  templateUrl: './thread-view.component.html',
  styleUrls: ['./thread-view.component.scss'],
  standalone: false,
})
export class ThreadViewComponent implements OnInit, AfterViewInit, OnDestroy {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private authUserService = inject(AuthUserService);
  private dialog = inject(MatDialog);
  private taskService = inject(TaskService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private fileService = inject(FileService);
  private menuService = inject(MenuService);
  private activityStatusService = inject(ActivityStatusService);
  private settingsService = inject(SettingsService);

  @Input() isTask?: true | undefined;
  @Input() orgId: string;
  @Input() refId: string;
  @Input() mode: ThreadOrTaskMode;
  @Input() context: 'inbox' | 'task-detail-page' | undefined;

  threadItem: ThreadItem;
  userId: string;
  org: Org;
  spaceId?: string;
  taskReadyForMessages = false;

  inboxLink: string;

  initialLoadResponse: BehaviorSubject<Message[]>;

  themeClass: ThemeString;

  spaceName = '';

  directUserArchived = false;
  closed = false;
  userNotInThread = false;
  disableMessageInput = false;

  placeHolderText = standardPlaceholderText;

  deleteThreadDialogReference;

  isTyping = false;

  initialTaskUserUpdates: (UserUpdate & { index: number })[] | undefined =
    undefined; // user update prior to the first message being sent

  displayMessages: DisplayMessage[] = [];
  messageSeensReplaySubject = new ReplaySubject<MessageSeen[]>(1);

  threadError?:
    | 'noSelectedUser'
    | 'threadNotFound'
    | 'userNotFound'
    | 'threadTypeUnknown'
    | 'unknownError';

  waitingForTempFileRefs: string[] = [];
  displayFiles: DisplayFile[] = [];

  titleFormControl = new FormControl('', {
    validators: [
      Validators.required,
      Validators.maxLength(threadTitleMaxLength),
    ],
    nonNullable: true,
  });

  newThread:
    | undefined
    | {
        mode: 'thread' | 'channel';
        userIds: string[];
        submitAttempted: boolean;
        title: string | undefined;
        formattedThreadTitle: string | undefined;
      };

  threadData: {
    title: string;
    currentUserArray: {
      user: OrgUser;
      activityStatus?: ActivityStatusStringType;
      activityStatusSubscription?: Subscription;
    }[];
  } = {
    title: '',
    currentUserArray: [],
  };

  typingUsers: OrgUser[] = [];

  textareaFocused = false;
  isGroup = false;
  loadingMessages = false;

  visibleIntersectionObserver: IntersectionObserver;
  loadMoreIntersectionObserver: IntersectionObserver;

  endOfThreadVisible = false;

  screenWidth = 1920;
  messageInputPaddingHeight = '0px';

  messageFormControl = new FormControl('', { nonNullable: true });
  subscriptions = new Subscription();
  @ViewChild('messageTopDiv') messageTopDiv: ElementRef;
  @ViewChild('messageContainer') messageContainer: ElementRef;
  @ViewChild('endOfThread') endOfThreadElement: ElementRef;
  @ViewChild('messageInput') messageInput: ElementRef;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;
  @ViewChild('imageDummy') imageDummy: ElementRef;
  @ViewChild('messageBox') messageBox: ElementRef;

  canSend = false;

  messageFiles: FileElement[] = [];

  ngOnInit(): void {
    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);

    const { orgId, refId, mode } = this;

    const userOrgObj = this.authUserService.getAuthUserOrgObj({ orgId });

    if (!userOrgObj) {
      this.threadError = 'noSelectedUser';
      return;
    }

    this.userId = userOrgObj.user._id;
    this.org = userOrgObj.org;

    this.threadItem = this.messageService.getThreadItem({ orgId, refId, mode });

    this.inboxLink = generateInboxPath({ orgId });

    this.threadData = {
      title: '',
      currentUserArray: [],
    };

    if (this.mode === 'task') {
      const handleTask = (task) => {
        if (this.spaceId !== task.spaceId) {
          this.spaceId = task.spaceId;
          const space = userOrgObj.org.spaces.find(
            (s) => s._id === this.spaceId
          );
          if (space) this.spaceName = space.name;
        }

        if (!this.taskReadyForMessages && task.createdAt) {
          this.taskReadyForMessages = true;
          this.getInitialMessages();
          this.initiateReceivingTypingStatus();
          this.initiateSendingTypingStatus();
        }
      };
      const getTaskSub = this.taskService
        .getTaskAsync({ orgId, taskId: refId })
        .subscribe(handleTask);
      this.subscriptions.add(getTaskSub);
      const taskUpdateSub = this.taskService
        .taskUpdates({ taskId: refId })
        .subscribe(handleTask);
      this.subscriptions.add(taskUpdateSub);
    } else if (this.threadItem.isNewThread) {
      if (this.threadItem.mode !== mode)
        throw new Error('There is an error in thread mode for a ');

      if (mode !== 'thread' && mode !== 'channel')
        throw new Error('Unsupported mode in isNewThread: ' + mode);

      this.newThread = {
        mode,
        userIds: [],
        submitAttempted: false,
        title: undefined,
        formattedThreadTitle: undefined,
      };

      const newThreadData = this.newThread;

      const titleChangeSubscription =
        this.titleFormControl.valueChanges.subscribe((value: string) => {
          if (typeof value === 'undefined') return;
          const newValue = cleanTitleExceptTrim(value); // Don't trim as the user may have just pressed the space button and is intending to add more characters
          // Only run the change if it needs to be changed
          if (value !== newValue) this.titleFormControl.setValue(newValue);

          newThreadData.title = newValue.trim();

          this.storeDraft();
        });
      this.subscriptions.add(titleChangeSubscription);
    } else {
      this.getInitialMessages();
      this.initiateReceivingTypingStatus();
      this.initiateSendingTypingStatus();
    }

    // This section is for user updates that happen prior to the first message sent
    if (this.threadItem.task) {
      const initialUpdates = this.threadItem.task.userUpdates.filter(
        (u) => u.messageNumber === 0
      );

      if (initialUpdates.length)
        this.initialTaskUserUpdates = initialUpdates.map((update, index) => ({
          ..._.cloneDeep(update),
          index,
        }));

      const userUpdateSub = this.threadItem.userUpdateObservable
        .pipe(filter((u) => u.messageNumber === 0))
        .subscribe((update) => {
          if (!this.initialTaskUserUpdates)
            return (this.initialTaskUserUpdates = [update]);

          let index = this.initialTaskUserUpdates.findIndex(
            (u) => update.updatedAt < u.updatedAt
          );
          if (index === -1) index = this.initialTaskUserUpdates.length;
          this.initialTaskUserUpdates.splice(index, 0, update);
        });
      this.subscriptions.add(userUpdateSub);
    }

    this.setupSubscriptions(this.threadItem);

    this.messageFormControl.valueChanges.subscribe(() =>
      this.manageSendButton()
    );

    this.screenWidth = window.innerWidth;
    this.readjustMessagePaddingForMessageInputHeight();
  }

  ngAfterViewInit(): void {
    this.createVisibleIntersectionObserver();
    this.createLoadMoreIntersectionObserver();
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.screenWidth = event.target.innerWidth;
    this.readjustMessagePaddingForMessageInputHeight();
  }

  getInitialMessages() {
    const query = { limit: 40 };
    this.initialLoadResponse = this.threadItem.getMessages(query);
    const getSub = this.initialLoadResponse.subscribe({
      next: (messages) => {
        this.insertMessages({ messages, scroll: 'firstUnread' });
      },
      error: (error) => {
        if (error.status === 404) {
          this.threadError = 'threadNotFound';
        } else {
          console.error(error);
          this.threadError = 'unknownError';
        }
      },
    });
    this.subscriptions.add(getSub);
  }

  handleUserIdsNewThread(userIds: string[]) {
    if (this.newThread) {
      this.newThread.userIds = userIds;
      this.storeDraft();
    }
  }

  submitOnEnter(event: KeyboardEvent): void {
    if (this.screenWidth > 768) {
      // Only do return on enter on desktop
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        this.messageBox.nativeElement.value += '\n';
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    }

    this.readjustMessagePaddingForMessageInputHeight();
  }

  onTextareaFocus(): void {
    this.textareaFocused = true;
    this.menuService.mobileToolbarHide();
  }

  onTextareaBlur(event): void {
    this.textareaFocused = false;
    this.menuService.mobileToolbarDisplay();
  }

  readjustMessagePaddingForMessageInputHeight() {
    setTimeout(() => {
      if (this.screenWidth > 768) {
        // padding should be zero
        this.messageInputPaddingHeight = '0px';
      } else {
        // The following line is to prevent an error if someone clicks off a page with 10ms and the element is gone
        if (!this?.messageInput?.nativeElement?.offsetHeight) return;

        // Get the height of message input, adjust padding
        this.messageInputPaddingHeight =
          this.messageInput.nativeElement.offsetHeight + 'px';
      }
    }, 10);
  }

  initiateReceivingTypingStatus(): void {
    const typingSubscribe = this.messageService.typingObservable.subscribe(
      (typingObject) => {
        if (typingObject.refId === this.refId) {
          if (typingObject.isTyping === true) {
            if (!this.typingUsers) this.typingUsers = [];
            if (typingObject.userId === this.userId) return; // Don't display self
            const foundTypingUser = this.typingUsers.find(
              (t) => t._id === typingObject.userId
            );
            if (!foundTypingUser) {
              const userToAdd = this.threadData?.currentUserArray.find(
                (u) => u.user._id === typingObject.userId
              );
              if (!userToAdd) throw new Error('typing user not found');
              this.typingUsers.push(userToAdd.user);
            }
          } else {
            const foundTypingUser = this.typingUsers?.find(
              (t) => t._id === typingObject.userId
            );
            if (!foundTypingUser || !this.typingUsers) return;

            const index = this.typingUsers.indexOf(foundTypingUser);
            if (index !== -1) this.typingUsers.splice(index, 1);
          }

          // TODO: should this be removed?
          setTimeout(() => {
            this.messageContainer.nativeElement.scrollTop =
              this.messageContainer.nativeElement.scrollHeight;
          }, 200);
        }
      }
    );
    this.subscriptions.add(typingSubscribe);
  }

  private createVisibleIntersectionObserver() {
    this.visibleIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const displayMessage = this.displayMessages.find(
            (d) => d.divId === entry.target.id
          );

          if (!displayMessage) return;
          displayMessage.isIntersecting = entry.isIntersecting;

          if (
            displayMessage.message?.number === this.threadItem.messageNumber
          ) {
            this.endOfThreadVisible = displayMessage.isIntersecting;
          }
        });
      },
      {
        root: this.messageContainer.nativeElement,
        rootMargin: '0px',
        threshold: 0,
      }
    );
  }

  private createLoadMoreIntersectionObserver() {
    this.loadMoreIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only run if intersecting
          if (!entry.isIntersecting) return;

          const displayMessage = this.displayMessages.find(
            (d) => d.divId === entry.target.id
          );

          if (!displayMessage) {
            console.error('matching display message not found');
            return;
          }
          if (!displayMessage.message) {
            console.error('matching message not set');
            return;
          }

          const end = displayMessage.message.number - 1;

          // If prior message exists, we don't get more and rely on the messages
          // above to come into view
          if (this.displayMessages.find((d) => d.message?.number === end)) {
            return;
          }

          // Get more results until the next visible message
          let start = end - 20;
          if (start < 1) start = 1;
          const sub = this.threadItem
            .getMessages({ start, end, limit: 20 })
            .subscribe((messages) => {
              this.insertMessages({ messages, scroll: 'fixed' });
            });
          this.subscriptions.add(sub);
        });
      },
      {
        root: this.messageContainer.nativeElement,
        rootMargin: '1000px 0px 1000px 0px',
        threshold: 0,
      }
    );
  }

  initiateSendingTypingStatus(): void {
    this.messageFormControl.valueChanges.subscribe((formData) => {
      const messageBoxText = this.messageBox?.nativeElement.value; // this.messageBox?. is necessary because loading problems
      if (!this.isTyping && messageBoxText) {
        // On typing status change from isTyping=false to true , make an API call
        this.typingApiCall(true);
      } else if (messageBoxText === '') {
        this.typingApiCall(false);
      }
    });

    // Stop typing indicator after 5 seconds
    this.messageFormControl.valueChanges
      .pipe(debounceTime(5000))
      .subscribe((formData) => this.typingApiCall(false));
  }

  typingApiCall(isTyping: boolean): void {
    if (!this.orgId || !this.refId) return;
    let changeInState = false;
    if (this.isTyping !== isTyping) {
      changeInState = true;
    }
    this.isTyping = isTyping;
    if (changeInState) {
      const reqBody: ThreadTypingIndicator.ReqBody = {
        isTyping: this.isTyping,
      };

      if (this.mode === 'task') {
        this.http
          .post<TaskTypingIndicator.ResBody>(
            generatePath<TaskTypingIndicator.ReqParams>(
              '/api/orgs/:orgId/tasks/:taskId/typing',
              { orgId: this.orgId, taskId: this.refId }
            ),
            reqBody
          )
          .subscribe((response) => {
            //
          });
      } else {
        this.http
          .post<ThreadTypingIndicator.ResBody>(
            generatePath<ThreadTypingIndicator.ReqParams>(
              '/api/orgs/:orgId/threads/:threadId/typing',
              { orgId: this.orgId, threadId: this.refId }
            ),
            reqBody
          )
          .subscribe((response) => {
            //
          });
      }
    }
  }

  uploadFile(): void {
    const imageInput = document.createElement('input');
    imageInput.setAttribute('type', 'file');
    imageInput.setAttribute('accept', 'image/png, image/gif, image/jpeg');
    this.imageDummy.nativeElement.appendChild(imageInput); // From IOS 12.2, image won't be uploaded unless the input is appended to DOM
    imageInput.addEventListener('change', () => {
      const files = imageInput.files;
      if (!files || files.length === 0) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const statusObservable = new Subject<'cancel' | 'remove'>();

        if (!this.orgId) throw new Error('orgId not set');
        const { progressObservable, messageFileObservable } =
          this.fileService.uploadFile({ file, orgId: this.orgId });

        const tempFileRef = generateId();
        this.waitingForTempFileRefs.push(tempFileRef);

        const displayFile: DisplayFile = {
          tempFileRef,
          file,
          progressObservable,
          messageFileObservable,
          statusObservable,
        };

        const messageFileSubscription = messageFileObservable.subscribe(
          (messageFile) => {
            this.messageFiles.push(messageFile);
            displayFile.fileId = messageFile._id;
            this.removeWaitingForFile(tempFileRef);
            this.storeDraft();
            this.manageSendButton();
          },
          (err) => {
            this.removeWaitingForFile(tempFileRef);
            this.storeDraft();
            this.manageSendButton();
          }
        );
        this.subscriptions.add(messageFileSubscription);

        const statusSubscription = statusObservable.subscribe((status) => {
          if (status === 'cancel' || status === 'remove') {
            this.removeWaitingForFile(tempFileRef);
            this.removeDisplayFileAndMessageFile(tempFileRef);
          }
        });
        this.subscriptions.add(statusSubscription);

        this.displayFiles.push(displayFile);

        this.storeDraft();
        this.manageSendButton();
      }
    });

    imageInput.click();
  }

  launchEditTitleDialog(): void {
    if (!this.threadData || !this.threadItem)
      throw new Error(
        'This should not be launched unless threadData is present'
      );
    const { authUserId, orgId, refId: threadId, mode } = this.threadItem;
    const dialogConfig = new MatDialogConfig();
    const data: ThreadEditTitleData = { authUserId, orgId, threadId, mode };
    dialogConfig.data = data;
    const dialogRef = this.dialog.open(
      ThreadEditTitleDialogComponent,
      dialogConfig
    );
    const editTitleSubscription = dialogRef.afterClosed().subscribe((res) => {
      // Use res if needed
    });
    this.subscriptions.add(editTitleSubscription);
  }

  launchRecipientsDialog(): void {
    if (!this.threadData || !this.threadItem)
      throw new Error(
        'This should not be launched unless threadData is present'
      );
    const { authUserId, orgId, refId, mode } = this.threadItem;
    const dialogConfig = new MatDialogConfig();
    const data: ThreadEditUsersData = { authUserId, orgId, refId, mode };
    dialogConfig.data = data;
    const dialogRef = this.dialog.open(
      ThreadEditUsersDialogComponent,
      dialogConfig
    );
    const editTitleSubscription = dialogRef.afterClosed().subscribe((res) => {
      // Use res if needed
    });
    this.subscriptions.add(editTitleSubscription);
  }

  private removeWaitingForFile(tempFileRef: string) {
    const index = this.waitingForTempFileRefs.indexOf(tempFileRef);
    if (index !== -1) this.waitingForTempFileRefs.splice(index, 1);
  }

  private removeDisplayFileAndMessageFile(tempFileRef: string) {
    if (!this.orgId) throw new Error('orgId not set');
    const displayFilesIndex = this.displayFiles.findIndex(
      ({ tempFileRef: x }) => x === tempFileRef
    );
    // If the file is already pushed into the messageFiles array
    const { fileId } = this.displayFiles[displayFilesIndex];
    if (fileId) {
      const messageFileIndex = this.messageFiles.findIndex(
        (f) => f._id === fileId
      );
      if (messageFileIndex !== -1)
        this.messageFiles.splice(messageFileIndex, 1);
      this.fileService.deleteFile({ orgId: this.orgId, fileId });
    }

    if (displayFilesIndex !== -1)
      this.displayFiles.splice(displayFilesIndex, 1);
  }

  manageSendButton() {
    let canSend = true;

    // Prevent send if files are still uploading
    if (this.waitingForTempFileRefs.length !== 0) canSend = false;

    if (this.newThread) {
      // If there is no title, don't send
      const title = this.titleFormControl.value;
      const cleanedTitle = cleanTitle(title);
      if (!cleanedTitle) canSend = false;
    }

    // One of body or files needed
    const body = this.messageFormControl.value?.trim();
    const filesExist = this.messageFiles.length !== 0;
    if (!body && !filesExist) canSend = false;

    this.canSend = canSend;
  }

  storeDraft() {
    const title: string | undefined = this.titleFormControl.value;
    const userIds: string[] | undefined = this.newThread?.userIds;
    const body: string = this.messageFormControl.value;
    const files = this.messageFiles;

    this.threadItem.storeDraft({ title, userIds, body, files });
  }

  setupSubscriptions(threadItem: ThreadItem): void {
    const titleSubscribe = threadItem.titleReplayObservable.subscribe(
      (title) => (this.threadData.title = title)
    );
    this.subscriptions.add(titleSubscribe);

    if (!this.orgId) throw new Error('orgId not set');
    const orgId = this.orgId;

    const userIdsSubscribe = threadItem.userIdsReplayObservable.subscribe(
      (userIds) => {
        // Clean up removed users
        this.threadData.currentUserArray.forEach((c) => {
          const removed = !userIds.find((userId) => userId === c.user._id);
          if (!removed) return;
          // Remove the user
          c.activityStatusSubscription?.unsubscribe();
          // Remove from userArray
          const index = this.threadData.currentUserArray.indexOf(c);
          if (index !== -1) this.threadData.currentUserArray.splice(index, 1);
          // Remove from typing array if set
          const i = this.typingUsers.findIndex((u) => u._id === c.user._id);
          if (i !== -1) this.typingUsers.splice(i, 1);
        });

        userIds.forEach((userId) => {
          const existingEntry = this.threadData.currentUserArray.find(
            (user) => user.user._id === userId
          );

          if (!existingEntry) {
            const user = this.authUserService.getUser({ orgId, userId });
            let entry: {
              user: OrgUser;
              activityStatus?: ActivityStatusStringType;
              activityStatusSubscription?: Subscription;
              typing: boolean;
            };
            if (!user) {
              entry = { user: unknownUser({ userId }), typing: false };
            } else {
              entry = { user, typing: false };
            }

            this.threadData.currentUserArray.push(entry);

            const activityStatusSubscription = this.activityStatusService
              .userActivityStatus({ orgId, userId })
              .subscribe((status) => {
                entry.activityStatus = status;
              });

            entry.activityStatusSubscription = activityStatusSubscription;

            this.subscriptions.add(activityStatusSubscription);
          }
        });

        // Move self to the end
        const index = this.threadData.currentUserArray.findIndex(
          (u) => u.user._id === this.userId
        );
        if (index !== -1) {
          const selfEntry = this.threadData.currentUserArray.splice(
            index,
            1
          )[0];
          if (selfEntry) this.threadData.currentUserArray.push(selfEntry);
        }

        this.isGroup = this.threadData.currentUserArray.length > 1;

        // Remove users who are no longer found
        const removeUserIds = this.threadData.currentUserArray
          .map((u) => u.user._id)
          .filter((u) => !userIds.includes(u));
        removeUserIds.forEach((u) =>
          this.threadData.currentUserArray.splice(
            this.threadData.currentUserArray.findIndex((t) => t.user._id === u),
            1
          )
        );

        // For auth users who have left the discussion
        if (this.mode === 'thread' && !userIds.includes(this.userId)) {
          this.userNotInThread = true;
          this.setMessageBox();
        } else {
          this.userNotInThread = false;
          this.setMessageBox();
        }

        // Disable direct message with archived users
        if (this.mode === 'direct') {
          const userId = determineDirectUserId({
            userIds,
            userId: this.userId,
            threadId: this.refId,
          });
          if (userId) {
            const user = this.authUserService.getUser({ orgId, userId });
            if (user?.archivedAt) {
              this.directUserArchived = true;
              this.setMessageBox();
            } else {
              this.directUserArchived = false;
              this.setMessageBox();
            }
          }
        }
      }
    );
    this.subscriptions.add(userIdsSubscribe);

    const messageSeensSubscribe =
      threadItem.messageSeensReplayObservable.subscribe((messageSeens) =>
        this.messageSeensReplaySubject.next(messageSeens)
      );
    this.subscriptions.add(messageSeensSubscribe);

    const liveMessagesSubscription = threadItem.liveMessageObservable.subscribe(
      ({ messages }) => this.insertMessages({ messages, scroll: 'endIfAtEnd' })
    );
    this.subscriptions.add(liveMessagesSubscription);

    const viewOnlySub = threadItem.closedObservable.subscribe((closed) => {
      if (closed) {
        this.closed = true;
        this.setMessageBox();
      } else {
        this.closed = false;
        this.setMessageBox();
      }
    });
    this.subscriptions.add(viewOnlySub);
  }

  sendMessage(): void {
    this.typingApiCall(false);
    const body = this.messageFormControl.value.trim();
    // Must send content or files
    if (!body && this.messageFiles.length === 0) return;

    // Creating a new thread
    if (this.newThread) {
      this.newThread.submitAttempted = true;

      let title: string | undefined = undefined;
      if (this.newThread.title) {
        title = this.newThread.title;
      }

      if (!title) {
        // Error in userIds is displayed via this.newThreadData.submitAttempted in recipient-editor
        if (!title) this.titleFormControl.setErrors({ incorrect: true });
        return;
      }

      const { messageObservable } = this.threadItem.createThread({
        userIds: this.newThread.userIds,
        title,
        message: { body, files: this.messageFiles },
      });

      // Once the message is sent, mark as not a newThread
      const messageSubscription = messageObservable.pipe(take(1)).subscribe({
        next: (message) => {
          this.newThread = undefined;
          this.insertMessages({ messages: [message], scroll: 'end' });
          this.resetMessage();
        },
        error: () => {
          // 422 or 500
          this.snackBar.open(
            'Message cannot be sent at this time, Please try later.',
            'Dismiss',
            { duration: 2000 }
          );
        },
      });
      this.subscriptions.add(messageSubscription);
    } else {
      const { messageSubmitted, messageObservable } =
        this.threadItem.sendMessage({ body, files: this.messageFiles });
      this.resetMessage();
      // We insert this into messages prior to being accepted by the server.
      // Anything that is updated by the server can be updated upon response.
      this.insertSubmittedMessage({
        message: messageSubmitted,
        messageObservable,
      });
    }
  }

  createChannel() {
    if (!this.newThread)
      throw new Error('Cannot create channel if !this.newThread');

    if (this.newThread.mode !== 'channel')
      throw new Error(
        'Tried to create a new channel in a non-channel thread mode'
      );

    const { title, userIds } = this.newThread;

    if (!title) {
      // Error in userIds is displayed via this.newThreadData.submitAttempted in recipient-editor
      if (!title) this.titleFormControl.setErrors({ incorrect: true });
      return;
    }

    const { threadObservable } = this.threadItem.createThread({
      userIds,
      title,
    });

    const threadSub = threadObservable.pipe(take(1)).subscribe({
      next: (message) => {
        this.newThread = undefined;
        this.resetMessage();
      },
      error: () => {
        // 422 or 500
        this.snackBar.open(
          'Channel cannot be created at this time, Please try later.',
          'Dismiss',
          { duration: 2000 }
        );
      },
    });
    this.subscriptions.add(threadSub);
  }

  resetMessage(): void {
    this.messageFormControl.reset();
    this.messageBox.nativeElement.focus();
    this.messageFiles = [];

    this.messageFormControl.setErrors(null);

    this.readjustMessagePaddingForMessageInputHeight();
  }

  closeDeleteThread(): void {
    this.deleteThreadDialogReference?.close();
  }

  deleteThread(): void {
    if (this.mode === 'task' || this.mode === 'space')
      throw new Error('Cannot delete task thread');

    const { orgId, refId: threadId } = this;

    this.messageService.deleteThread({ orgId, threadId }).subscribe({
      next: () => {
        this.deleteThreadDialogReference?.close();
        this.snackBar.open('Thread deleted successfully!', 'Dismiss', {
          duration: 2000,
        });
        this.router.navigate([this.inboxLink]);
      },
      error: () => {
        this.snackBar.open(
          'Thread deletion error, please try later.',
          'Dismiss',
          {
            duration: 2000,
          }
        );
      },
    });
  }

  insertMessages({
    messages,
    scroll,
  }: {
    messages: Message[];
    scroll: 'firstUnread' | 'end' | 'endIfAtEnd' | 'fixed';
  }): void {
    const endOfThreadVisible = this.endOfThreadVisible;

    if (scroll === 'endIfAtEnd') scroll = endOfThreadVisible ? 'end' : 'fixed';

    let initialScrollPosition:
      | { offsetTop: number; divId: string; scrollTop: any }
      | undefined = undefined;
    if (scroll === 'fixed') {
      // Maintain position of scroll
      initialScrollPosition = this.determineScrollPosition();
    }

    messages.forEach((message) => {
      // If already exists, skip it
      const exists = this.displayMessages.find(
        (d) => d.message && d.message.number === message.number
      );
      if (exists) return;

      let index = this.displayMessages.findIndex((d) => {
        if (d.message) {
          if (d.message.number > message.number) {
            return true;
          }
        }
      });

      // If not set, put at the end
      if (index === -1) index = this.displayMessages.length; // #indexCheck-insertMessages

      // Determine if the immediate previous neighbor to this message is the
      // actual neighbor
      let previousMessage: Message | undefined = undefined;
      if (
        index > 0 &&
        this.displayMessages[index - 1].message &&
        this.displayMessages[index - 1].message?.number === message.number - 1
      ) {
        previousMessage = this.displayMessages[index - 1].message;
      }

      const displayMessage: DisplayMessage = {
        divId: generateId(),
        visible: true,
        isIntersecting: false,
        message,
        previousMessage: new BehaviorSubject(previousMessage),
      };

      // Determine if the immediate subsequent neighbor to this message is the
      // actual neighbor, update their previous message
      const entryAtIndex = this.displayMessages[index];
      if (
        entryAtIndex &&
        entryAtIndex.message &&
        entryAtIndex.message.number === message.number + 1
      ) {
        entryAtIndex.previousMessage.next(message);
      }

      // Insert into the array
      this.displayMessages.splice(index, 0, displayMessage); // #indexCheck-insertMessages

      this.observerIntersection(displayMessage);
    });

    setTimeout(() => {
      if (scroll === 'firstUnread') this.scrollToFirstUnreadMessage();
      if (scroll === 'fixed' && initialScrollPosition)
        this.scrollToOldPosition(initialScrollPosition);
      if (scroll === 'end') this.scrollToEnd();
    }, 1);
  }

  insertSubmittedMessage({
    message,
    messageObservable,
  }: {
    message: MessageSubmitted;
    messageObservable: Observable<Message>;
  }): DisplayMessage {
    let previousMessage: Message | undefined = undefined;
    if (this.displayMessages.length > 0) {
      const lastMessage = this.displayMessages[this.displayMessages.length - 1];
      const number = message.number;
      if (lastMessage.message && lastMessage.message?.number === number - 1) {
        previousMessage = lastMessage.message;
      }
    }
    const displayMessage: DisplayMessage = {
      divId: generateId(),
      visible: true,
      isIntersecting: false,
      message,
      messageObservable,
      previousMessage: new BehaviorSubject(previousMessage),
    };
    this.displayMessages.push(displayMessage);

    const sub = messageObservable.subscribe((m) => {
      displayMessage.message = m;

      sub.unsubscribe();
    });
    this.subscriptions.add(sub);

    this.observerIntersection(displayMessage);

    // Scroll to end
    setTimeout(() => this.scrollToEnd(), 1);

    return displayMessage;
  }

  private observerIntersection(displayMessage: DisplayMessage, attempt = 0) {
    const ele = document.getElementById(displayMessage.divId);

    if (ele) {
      this.visibleIntersectionObserver.observe(ele);
      this.loadMoreIntersectionObserver.observe(ele);
    } else {
      // Try again after a bit
      const time = [10, 50, 100, 500, 1000, 5000, 10000][attempt];
      attempt++;
      if (!time) {
        // could not observe element, might not be created yet or about to be destroyed
        return;
      }
      setTimeout(() => this.observerIntersection(displayMessage, time), time);
    }
  }

  private determineScrollPosition() {
    // Determine the first visible element
    const firstVisible = this.displayMessages.find((d) => d.isIntersecting);
    if (!firstVisible) return;
    const { divId } = firstVisible;
    const ele = document.getElementById(divId);
    if (!ele) return;

    // Get the offsetTop
    const offsetTop = ele.offsetTop;

    // Get the scrollTop of the parent
    const scrollTop = this.messageContainer.nativeElement.scrollTop;

    // If the offsetTop changes, then change the scrollTop by the same amount

    return { offsetTop, divId, scrollTop };
  }

  private scrollToOldPosition({
    offsetTop,
    divId,
    scrollTop,
  }: {
    offsetTop: number;
    divId: string;
    scrollTop: number;
  }) {
    // Get the new offset height
    const ele = document.getElementById(divId);
    if (!ele) return;
    const newOffsetTop = ele.offsetTop;

    const offsetDiff = newOffsetTop - offsetTop;
    const calculatedScrollTop = scrollTop + offsetDiff;

    // We do this here because Firefox pushes items down in the scroll box,
    // while Chrome does not.
    if (calculatedScrollTop !== this.messageContainer.nativeElement.scrollTop) {
      this.messageContainer.nativeElement.scrollTop = calculatedScrollTop;
    }
  }

  private scrollToFirstUnreadMessage() {
    // Get the latest seen message
    const seen = this.threadItem.messageSeens.find(
      (s) => s.userId === this.userId
    );
    if (!seen) {
      // Scroll instantly to end
      this.endOfThreadElement.nativeElement.scrollIntoView(false);
    } else {
      // Scroll instantly to top of latest seen message (because we want a little bit of context)
      this.scrollToMessage({ number: seen.number });
    }
  }

  private scrollToMessage({ number }: { number: number }) {
    // Determine the first visible element
    const displayMessage = this.displayMessages.find(
      (d) => d.message?.number === number
    );
    if (!displayMessage) return;
    const { divId } = displayMessage;
    const ele = document.getElementById(divId);
    if (!ele) return;

    // Get the offsetTop
    const offsetTop = ele.offsetTop;

    this.messageContainer.nativeElement.scrollTop = offsetTop;
  }

  private scrollToEnd() {
    this.endOfThreadElement.nativeElement.scrollIntoView({
      behavior: 'smooth',
      inline: 'end',
      block: 'start',
    });
  }

  clickMobileHeader() {
    if (this.mode === 'task') {
      const { orgId, refId: taskId } = this;
      let path: string;

      if (this.context === 'inbox') {
        path = generateTaskInboxPath({ orgId, taskId });
      } else {
        path = generateTaskPath({ orgId, taskId });
      }
      this.router.navigate([path]);
    } else {
      this.launchEditTitleDialog();
    }
  }

  private setMessageBox() {
    let disableMessageInput = true;

    if (this.directUserArchived) {
      this.placeHolderText = 'Messages cannot be sent to archived users';
    } else if (this.closed) {
      this.placeHolderText = 'This thread is closed';
    } else if (this.userNotInThread) {
      this.placeHolderText = 'You are no longer in this thread';
    } else {
      this.placeHolderText = standardPlaceholderText;
      disableMessageInput = false;
    }
    if (this.disableMessageInput !== disableMessageInput) {
      this.disableMessageInput = disableMessageInput;
      if (this.disableMessageInput) this.messageFormControl.disable();
      else this.messageFormControl.disable();
    }
  }

  reloadPage(): void {
    window.location.reload();
  }

  ngOnDestroy(): void {
    const title = this.newThread?.title;
    const userIds = this.newThread?.userIds;
    const body = this.messageFormControl.value.trim();
    const files = this.messageFiles;

    // Potentially save draft
    if (title || userIds?.length || body || files.length)
      this.threadItem.storeDraft({ title, userIds, body, files });

    // Ensure mobile toolbar is shown after nav
    this.menuService.mobileToolbarDisplay();

    this.subscriptions.unsubscribe();
    this.visibleIntersectionObserver.disconnect();
    this.loadMoreIntersectionObserver.disconnect();
  }
}
